import { TitleCasePipe } from "@angular/common";
import {
    Component,
    computed,
    effect,
    inject,
    signal,
    untracked,
    viewChild,
    viewChildren,
    ChangeDetectionStrategy,
} from "@angular/core";
import { HttpRequestOptions } from "../../dataaccess/data.access";
import { Bind } from "../../decorators/bind.decorator";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { HasItemType, Item } from "../../pojo/Item";
import { Report } from "../../pojo/Report";
import { Nullable, Refreshable } from "../../pojo/Util";
import { AriaLive } from "../../services/aria.live.service";
import { AvailService } from "../../services/avail.service";
import { ContactService } from "../../services/contact.service";
import { EventService } from "../../services/event.service";
import { NotificationService } from "../../services/notification.service";
import { ReportService, ReportType } from "../../services/report.service";
import { SearchUtil } from "../../services/search/s25.search.util";
import { SearchService } from "../../services/search/search.service";
import { StateService } from "../../services/state.service";
import { TelemetryService } from "../../services/telemetry.service";
import { CalendarWithOptions } from "../../standalone/calendar/calendar.with.options.component";
import { LoginStore } from "../../stores/login.store";
import { SearchStore } from "../../stores/search.store";
import { S25Const } from "../../util/s25-const";
import { S25Util } from "../../util/s25-util";
import { S25Assert } from "../../util/s25.assert";
import { S25MultiAvailWeeklyComponent } from "../s25-avail-weekly/s25.multi.avail.weekly.component";
import { S25AvailabilityGridComponent } from "../s25-availability/s25.availability.grid.component";
import { S25AvailabilityModule } from "../s25-availability/s25.availability.module";
import { S25AiSearchChatbotComponent } from "../s25-chat-bot/s25.search.chat.bot.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import {
    ReloadAndSelectParams,
    ReloadParams,
    S25SearchDropdownComponent,
} from "../s25-dropdown/single-select/s25.search.dropdown.component";
import { ModalService } from "../modal/modal.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { S25ModalModule } from "../s25-modal/s25.modal.module";
import { S25ObjectModule } from "../s25-object/s25.object.module";
import { S25PublisherModule } from "../s25-publisher/s25.publisher.module";
import { S25SecurityLinkModule } from "../s25-security-link/s25.security.link.module";
import { SidebarStore } from "../s25-sidebar/s25.sidebar.store";
import { S25ToggleButtonModule } from "../s25-toggle-button/s25.toggle.button.module";
import { S25ModalShareSearchComponent } from "./s25.modal.share.search.component";
import { S25SearchResultsListComponent } from "./s25.search.results.list.component";
import { AdvancedModel } from "./s25ql.search.advanced.criteria.component";
import { S25QLSearchAdvancedInputComponent } from "./s25ql.search.advanced.input.component";
import { S25qlSearchInputComponent } from "./s25ql.search.input.component";
import { SimpleSearch } from "./simple.search.criteria";
import { S25qlSearchSimpleInputComponent } from "./s25ql.search.simple.input.component";
import { S25QLTokenizer } from "./s25ql.tokenizer";
import { SaveSearchModalComponent } from "./save.search.modal.component";
import { ComplexSearch, Search } from "./search";
import { SearchTelemetry } from "./search.telemetry";
import Ids = Item.Ids;

type NoSearchDef = {
    readonly prefixedQuery?: never;
    readonly unPrefixedQuery?: never;
    readonly complex?: never;
    readonly queryGenerator?: never;
    readonly unPrefixedQueryGenerator?: never;
    readonly tempSearchFn?: never;
};
type SimpleSearchDef = {
    // frustratingly, we need both queries for now.
    /**
     * This query has prefixes added, needed for calendar
     */
    readonly prefixedQuery: string;
    /**
     * This query has prefixes stripped, used for lists
     */
    readonly unPrefixedQuery: string;
    readonly complex: ComplexSearch;
    readonly queryGenerator?: never;
    readonly unPrefixedQueryGenerator?: never;
    readonly tempSearchFn?: never;
};
type ComplexSearchDef = {
    readonly prefixedQuery?: never;
    readonly unPrefixedQuery?: never;
    readonly complex: ComplexSearch;
    readonly queryGenerator: any;
    readonly unPrefixedQueryGenerator: any;
    readonly tempSearchFn: any;
};
type SearchDefinition = NoSearchDef | SimpleSearchDef | ComplexSearchDef;
type SearchConfig = {
    readonly rendered: readonly Search.Tab[];
    /**
     * "detaching" here means we want the search results to keep showing up,
     * but the user is no longer configuring this set of search results.
     * e.g. they've clicked "Reset", and we're still showing their last search
     * results while they start configuring a new search. This is used to treat
     * user inputs as pristine and unsubmitted until they try to search
     * with invalid input.
     */
    readonly detached?: boolean;
    /**
     * Has the saved searches dropdown finished loading for this item type?
     */
    readonly savedSearchesLoaded?: boolean;
} & SearchDefinition;

const emptySearchByType = Object.fromEntries(
    Search.allTypeIds.map((typeId): [Search.TypeId, SearchConfig] => [typeId, { rendered: [] }]),
) as Readonly<Record<Search.TypeId, Readonly<SearchConfig>>>;

type HasValidSavedSearch = {
    // I think "base"/"gerund" would be less clear than "verb"/"verbing"
    verb: string;
    verbing?: string;
    requireOwner?: boolean;
    requireName?: boolean;
};

const REPORT_LIMIT = 1500;

type UrlState = { viz: Search.Tab; search: string | number; subject: Search.Type };

@TypeManagerDecorator("s25-ng-home-search")
@Component({
    selector: "s25-ng-home-search",
    template: `
        <div class="home-search">
            <div class="search-controls" [attr.data-type]="store.typeName()">
                <div class="searchChooser flex center">
                    <label for="objectTypeChooser">Select Object:</label>
                    <s25-generic-dropdown
                        class="itemTypeDropdown"
                        [items]="itemTypeOptions()"
                        [value]="chosenItemType()"
                        (valueChange)="itemTypeChosen($event)"
                        [choice]="objectTypeDropdownChoice"
                        [useChoiceForChosen]="true"
                        [ariaLabel]="'Select Object'"
                    />
                    <ng-template #objectTypeDropdownChoice let-item="item">
                        <s25-ng-icon [type]="item?.itemName" [autoDarkMode]="true" [ariaLabel]="false" />
                        {{ $safeNavigationMigration(item?.itemName) + "s" | titlecase }}
                    </ng-template>

                    <s25-ng-search-dropdown
                        [hasFav]="true"
                        [value]="store.fromSavedSearch()"
                        (valueChange)="savedSearchChosen($event)"
                        [itemTypeId]="store.typeId()"
                        [onLoad]="setSavedSearchesLoaded"
                        [allowNonQueryId]="true"
                        [searchEnabled]="true"
                        [placeholder]="'Saved Searches (optional)'"
                        [ariaLabel]="'Saved Searches'"
                    />
                </div>

                @if (
                    loginStore.loggedIn() &&
                    !store.isPredefinedSearch() &&
                    (!store.fromSavedSearch() || store.fromSavedSearch()?.itemId) &&
                    store.canAdvancedSearch()
                ) {
                    <s25-toggle-button
                        [value]="advancedToggle()"
                        (valueChange)="onAdvancedToggleChange($event)"
                        [falseLabel]="'Quick Search'"
                        [trueLabel]="'Advanced'"
                        [disabled]="!!store.loadingSavedSearchCriteria()?.isLoading()"
                    />
                }

                @if (togglingAdvanced()) {
                    <s25-ng-loading-inline-static />
                } @else {
                    <div class="searchInput">
                        @if (!store.advancedSearch()) {
                            <s25-ng-ql-search-input
                                [value]="store.text() ?? ''"
                                (valueChange)="store.setTextInput($event)"
                                [type]="store.typeId()"
                                [placeholder]="'Search ' + (store.typeName() + 's' | titlecase)"
                                (search)="onSearch()"
                                (errorMessage)="store.setError($event)"
                                [disabled]="!!store.savedSearch()"
                            />
                        } @else {
                            <s25-ng-ql-search-advanced-input
                                #advancedCriteriaInput
                                [type]="store.typeId()"
                                [model]="$safeNavigationMigration(store.advancedSearch()?.model)!"
                                [searches]="$safeNavigationMigration(store.advancedSearch()?.searches)!"
                                (change)="refreshAdvancedSearch()"
                                class="c-padding-bottom--single ngBlock"
                            />
                        }
                    </div>
                }

                @if (
                    store.searchIsDirty() &&
                    hasSearchResults() &&
                    !searchDetached() &&
                    loginStore.loggedIn() &&
                    store.buttonPerms().saveOrSaveAs &&
                    !store.savedSearch()
                ) {
                    <div class="saveMessage">
                        <span class="c-warning">Search has not been saved</span>
                    </div>
                }

                <div class="buttons">
                    @if (loginStore.loggedIn() && store.buttonPerms().create) {
                        <s25-ng-create-object
                            [data]="{
                                objectTypeId: $any(store.typeId()),
                                objectName: $any(store.typeName() | titlecase),
                            }"
                            class="borderRight"
                        />
                    }
                    @if (store.searchHasValue()) {
                        <button class="c-textButton borderRight" (click)="onReset()">Reset</button>
                    }
                    @if (
                        store.buttonPerms().runReport === "verified" &&
                        hasSearchResults() &&
                        store.tab() === "list" &&
                        (!store.isPredefinedSearch() || store.typeId() === Item.Ids.Task) &&
                        (!store.fromSavedSearch() ||
                            store.fromSavedSearch()?.itemId ||
                            store.typeId() === Item.Ids.Task)
                    ) {
                        <button class="c-textButton borderRight" (click)="onExport()">
                            @if (exporting()) {
                                <s25-ng-loading-inline-static />
                            } @else {
                                <span>Export Results</span>
                            }
                        </button>
                    }
                    @if (store.securityLink() && store.fromSavedSearch()) {
                        <button class="c-textButton borderRight">
                            <s25-security-link
                                [query]="true"
                                [itemTypeId]="store.typeId()"
                                [itemId]="store.fromSavedSearch()!.itemId!"
                                [groupAttr]="{
                                    queryVal: store.fromSavedSearch()!.val,
                                    itemName: store.fromSavedSearch()!.itemName,
                                }"
                            />
                        </button>
                    }
                    @if (store.buttonPerms().save && store.fromSavedSearch()?.itemId) {
                        <button class="c-textButton borderRight" (click)="save(false)">Save</button>
                    }
                    @if (showSaveAs()) {
                        <button class="c-textButton borderRight" (click)="save(true)">Save as</button>
                    }
                    <button class="aw-button aw-button--primary" (click)="onSearch()">Search</button>
                </div>

                @if (store.isKeywordSearching()) {
                    <s25-ng-ql-search-simple-input
                        #simpleCriteriaInput
                        [type]="store.typeId()"
                        [criteria]="$safeNavigationMigration(store.keywordSearch()?.criteria)"
                        (criteriaChange)="store.setCriteria($event)"
                    />
                }

                @if (store.errorMessage()) {
                    <div class="errorMessage">
                        <pre class="ngBold">Error: {{ store.errorMessage() }}</pre>
                    </div>
                }

                @if (store.fromSavedSearch()?.itemId && searchActions()?.length) {
                    <s25-generic-dropdown
                        class="searchActions"
                        [items]="searchActions()"
                        [noSelection]="'Search Actions'"
                        [placeholder]="'Search Actions'"
                        [(value)]="searchAction"
                        (valueChange)="handleSearchAction($event)"
                    />
                }
            </div>

            <div class="ngQLSearch">
                <h1 class="select2-hidden-accessible">Search</h1>
                <div class="c-grid c-grid--full-width">
                    <div class="ngQLSearchResultsArea c-grid__item--max">
                        @for (listTypeId of renderedVizTypes().list; track listTypeId) {
                            <s25-ng-search-results-list
                                [hidden]="!(store.typeId() === listTypeId && store.tab() === 'list')"
                                [itemTypeId]="listTypeId"
                                [query]="searchByType()[listTypeId]!.unPrefixedQuery"
                                [queryGenerator]="searchByType()[listTypeId]!.unPrefixedQueryGenerator"
                                [searchModel]="$safeNavigationMigration(searchByType()[listTypeId]!.complex?.model)"
                                (tabChange)="onTabChange($event)"
                            />
                        }
                        @for (calTypeId of renderedVizTypes().calendar; track calTypeId) {
                            <s25-ng-calendar-opt
                                [hidden]="!(store.typeId() === calTypeId && store.tab() === 'calendar')"
                                [comptype]="calTypeId === Item.Ids.Task ? 'cal_task_search' : 'calendar'"
                                [compsubject]="calTypeId === Item.Ids.Task ? 'event' : itemId2Name[calTypeId]"
                                [modelBean]="store.stateByType()[calTypeId].calBean"
                                [tempSearchFn]="searchByType()[calTypeId]!.tempSearchFn"
                                [searchQuery]="searchByType()[calTypeId]!.prefixedQuery!"
                                (tabChange)="onTabChange($event)"
                            />
                        }
                        @for (availabilityTypeId of renderedVizTypes().availability; track availabilityTypeId) {
                            <s25-ng-availability-grid
                                [hidden]="!(store.typeId() === availabilityTypeId && store.tab() === 'availability')"
                                [dataService]="AvailService.availDataService"
                                [view]="
                                    availabilityTypeId === Item.Ids.Event ? 'availability_schedule' : 'availability'
                                "
                                [itemType]="availabilityTypeId"
                                [query]="searchByType()[availabilityTypeId]!.prefixedQuery"
                                [queryGenerator]="searchByType()[availabilityTypeId]!.queryGenerator"
                                (tabChange)="onTabChange($event)"
                            />
                        }
                        @for (weeklyTypeId of renderedVizTypes().availabilityWeekly; track weeklyTypeId) {
                            <s25-ng-multi-avail-weekly
                                [hidden]="!(store.typeId() === weeklyTypeId && store.tab() === 'availabilityWeekly')"
                                [query]="searchByType()[weeklyTypeId]!.prefixedQuery"
                                [queryGenerator]="searchByType()[weeklyTypeId]!.queryGenerator"
                                [tab]="'availabilityWeekly'"
                                (tabChange)="onTabChange($event)"
                            />
                        }
                    </div>
                </div>
            </div>

            <s25-ng-modal #shareSearchModal [title]="'Share Search'">
                <ng-template #s25ModalBody>
                    @if (store.fromSavedSearch()) {
                        <s25-ng-modal-share-search
                            [searchName]="store.fromSavedSearch()!.itemName!"
                            [searchId]="store.fromSavedSearch()!.itemId!"
                            [itemType]="store.typeId()"
                            [unsavedChanges]="!store.savedSearch()"
                            (close)="shareSearchModal.close()"
                        />
                    }
                </ng-template>
            </s25-ng-modal>

            <s25-ng-publish-search-modal
                #publishSearchModal
                [itemType]="store.typeId()"
                [searchQuery]="$any($safeNavigationMigration(store.fromSavedSearch()?.val))"
                [feedType]="'r25_group'"
                [searchName]="$safeNavigationMigration(store.fromSavedSearch()?.itemName)"
                [unsavedChanges]="!store.savedSearch()"
            />

            <s25-ng-modal #exportResultsModal [title]="'Select a report to download'" [size]="'xxs'">
                <ng-template #s25ModalBody>
                    @for (report of reportOptions() ?? []; track report.report_id) {
                        <a class="ngBlock" [tabIndex]="0" (click)="exportResultsModal.close(report.report_id)">
                            <span class="ngReport ngMinImgDim ngImgText"></span>
                            <span>{{ report.report_name }}</span>
                        </a>
                    }
                </ng-template>
            </s25-ng-modal>
        </div>
        <s25-ng-ai-search-chatbot (searchQuery)="onAiSearchQuery($event)" />
    `,
    styleUrls: ["./s25.home.search.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        S25PublisherModule,
        S25ModalModule,
        S25ModalShareSearchComponent,
        S25MultiAvailWeeklyComponent,
        S25AvailabilityModule,
        S25SearchResultsListComponent,
        S25DropdownPaginatedModule,
        S25qlSearchSimpleInputComponent,
        S25LoadingInlineStaticComponent,
        S25SecurityLinkModule,
        S25ObjectModule,
        S25QLSearchAdvancedInputComponent,
        S25qlSearchInputComponent,
        S25ToggleButtonModule,
        S25IconComponent,
        TitleCasePipe,
        CalendarWithOptions,
        S25AiSearchChatbotComponent,
    ],
})
export class S25HomeSearchComponent {
    readonly shareSearchModal = viewChild.required<S25ModalComponent>("shareSearchModal");
    readonly exportResultsModal = viewChild.required<S25ModalComponent<number | undefined>>("exportResultsModal");
    readonly searchDropdown = viewChild.required(S25SearchDropdownComponent);
    readonly advancedInputComponent = viewChild(S25QLSearchAdvancedInputComponent);
    readonly publishSearchModal = viewChild<S25ModalComponent>("publishSearchModal");
    readonly listComponents = viewChildren(S25SearchResultsListComponent);
    readonly calendarComponents = viewChildren(CalendarWithOptions);
    readonly availGridComponents = viewChildren(S25AvailabilityGridComponent);
    readonly availWeeklyComponents = viewChildren(S25MultiAvailWeeklyComponent);

    private readonly sidebarStore = inject(SidebarStore);
    readonly loginStore = inject(LoginStore);
    readonly store = inject(SearchStore);

    readonly itemTypeOptions = computed<DropDownItem<Search.TypeId>[]>(() =>
        this.store.allowedItemTypes().map((itemTypeId) => ({
            itemId: itemTypeId,
            typeId: itemTypeId,
            itemName: S25Const.itemId2Name[itemTypeId],
        })),
    );
    readonly chosenItemType = computed(() =>
        this.itemTypeOptions().find((item) => item.itemId === this.store.typeId()),
    );
    readonly searchByType = signal(emptySearchByType);
    readonly search = signal<ComplexSearch | string | undefined>(undefined);
    readonly searchDefinition = computed((): SearchDefinition => {
        const search = this.search();
        if (!search) return {} as NoSearchDef;
        const typeId = this.store.typeId();
        if (Search.isComplex(search)) {
            // TODO: update calendar component to use queryGenerator approach
            const tempSearchFn = (httpOptions?: HttpRequestOptions) =>
                SearchService.createSearch({
                    model: S25Util.deepCopy(search.model),
                    searches: S25Util.deepCopy(search.searches),
                    typeId,
                    httpOptions,
                }).then((data) => ({
                    rerun: tempSearchFn,
                    invalidSearch: false,
                    searchQuery: SearchUtil.queryId2Query(data.queryId, true, typeId, typeId),
                    searchDelete: () => Promise.all(data.queryIdArr.map((q) => SearchService.deleteSearch(q, typeId))),
                }));
            return {
                queryGenerator: SearchUtil.getTempSearchQueryGenerator(search.model, search.searches, typeId, true),
                unPrefixedQueryGenerator: SearchUtil.getTempSearchQueryGenerator(
                    search.model,
                    search.searches,
                    typeId,
                    false,
                ),
                complex: search,
                tempSearchFn,
            } satisfies ComplexSearchDef;
        } else {
            return {
                prefixedQuery: SearchService.fixSearchQueryPrefixes({
                    itemTypeId: typeId,
                    query: search,
                    includePrefixes: true,
                    encode: false,
                }),
                unPrefixedQuery: SearchService.fixSearchQueryPrefixes({
                    itemTypeId: typeId,
                    query: search,
                    includePrefixes: false,
                    encode: false,
                }),
                complex: this.store.complexSearch(),
            } satisfies SimpleSearchDef;
        }
    });
    readonly searchConfig = computed(() => this.searchByType()[this.store.typeId()]);
    readonly savedSearchesLoaded = computed(() => this.searchConfig().savedSearchesLoaded);
    readonly hasSearchResults = computed(() => {
        const search = this.searchConfig();
        return search.prefixedQuery || search.queryGenerator || search.complex || search.tempSearchFn;
    });
    readonly searchDetached = computed(() => this.searchConfig()?.detached);
    readonly searchQueryString = computed(() => this.computeQueryString());
    readonly urlState = signal<UrlState | undefined>(undefined);
    readonly renderedVizTypes = computed<Record<Search.Tab, Search.TypeId[]>>(() => this.computeRenderedVizTypes());
    readonly advancedToggle = computed(() => (this.store.savedSearch() ? undefined : !!this.store.advancedSearch()));
    readonly togglingAdvanced = signal(false);
    readonly searchAction = signal<DropDownItem<Search.Action> | undefined>(undefined);
    readonly searchActions = computed(() => this.computeSearchActions());
    readonly exporting = signal(false);
    readonly reportOptions = signal<Report.Object[] | undefined>(undefined);
    readonly showSaveAs = computed(
        () =>
            this.store.buttonPerms().saveAs &&
            // always allow "Save as" if we have a saved search selected (NOT predefined)
            (this.store.fromSavedSearch()?.itemId ||
                // or allow "Save as" if we've started editing an advanced or keyword search
                (this.store.searchIsDirty() && !this.store.fromSavedSearch())),
    );

    readonly itemId2Name = S25Const.itemId2Name;
    protected readonly Item = Item;
    protected readonly AvailService = AvailService;

    readonly availabilitySession = computed(() =>
        SearchTelemetry.availabilitySession(this.store.typeId(), this.store.tab()),
    );

    constructor() {
        effect((onCleanup) => {
            const session = this.availabilitySession();
            if (!session) return;
            session.open();
            onCleanup(() => session.close());
        });
    }

    public routeChange(params: UrlState) {
        this.urlState.set(params);
        this.store.setItemType(S25Const.itemName2Id[params.subject] ?? Item.Ids.Event);
        // If we land SPECIFICALLY on /home/search (no viz, no search), we want to
        // revert to any previous search, so let's repopulate our search into the URL
        if (!params.viz && !params.search && this.hasSearchResults()) {
            // with a timeout, because we're in the middle of responding to a URL change
            setTimeout(() => this.updateUrl());
        } else {
            if (Search.allTabValues.includes(params.viz as Search.Tab)) {
                this.store.updateItemState({ tab: params.viz });
            } else {
                this.store.updateItemState({ tab: "list" });
            }
            this.processInitSearch(S25Util.isDefined(params.search) ? String(params.search) : undefined);
        }
    }

    private computeQueryString(): string | undefined {
        if (this.searchDetached()) {
            return "";
        } else if (this.store.savedSearch()) {
            const savedSearch = this.store.savedSearch()!;
            return String(savedSearch.chosen.itemId ?? savedSearch.chosen.val ?? "");
        } else if (this.searchConfig().prefixedQuery) {
            return this.searchConfig().prefixedQuery!;
        } else if (this.store.fromSavedSearch()) {
            const savedSearch = this.store.fromSavedSearch()!;
            return String(savedSearch.itemId ?? savedSearch.val ?? "");
        }
        // TODO: enable QL and advanced searches in URL?
        // } else if (this.searchConfig().queryGenerator) {
        //     const asQL = this.store.searchAsQL();
        //     if (asQL && asQL.length < 1500) {
        //         return (this.store.advancedSearch() ? "advanced" : "ql") + asQL;
        //     }
        // }
    }

    private updateUrlIfNeeded() {
        const params = this.urlState();
        const newParams = this.computeParams();
        if (
            params?.viz !== newParams.viz ||
            params?.subject !== newParams.subject ||
            params?.search !== newParams.search
        ) {
            this.updateUrl();
        }
    }

    private updateUrl() {
        this.urlState.set(this.computeParams());
        return StateService.go(".", this.urlState(), {
            source: "home-search-update-state",
            location: "replace",
        });
    }

    private computeParams() {
        return {
            viz: this.store.tab(),
            subject: this.store.typeName(),
            search: this.searchQueryString() ?? "",
        };
    }

    validate(): boolean {
        this.store.clearError();
        if (this.store.advancedSearch()) {
            return this.validateAdvancedSearch();
        } else if (this.store.rawQLSearch()) {
            return this.store.validateQLSearch();
        } else if (this.store.keywordSearch()) {
            return this.store.validateKeywordSearch();
        } else if (this.store.savedSearch()) {
            return true;
        }
        this.store.setError("No search provided.");
        return false;
    }

    public executeSearch() {
        if (this.store.rawQLSearch() || this.store.advancedSearch()) {
            this.search.set(this.store.complexSearch());
        } else if (this.store.keywordSearch()) {
            this.search.set(this.store.keywordParam() + this.store.criteriaParams());
        } else if (this.store.savedSearch()) {
            const savedSearchOption = this.store.savedSearch()!.chosen;
            const typeId = (savedSearchOption.itemTypeId ?? this.store.typeId()) % 100;
            if (typeId !== this.store.typeId()) {
                this.store.setItemType(typeId);
            }
            this.search.set(savedSearchOption.val ? String(savedSearchOption.val) : "");
        } else {
            throw new Error("No search configuration detected");
        }

        const typeId = this.store.typeId();
        const typeName = S25Util.firstCharToUpper(S25Const.itemId2Name[typeId]);
        const def = this.searchDefinition();
        if (Search.isComplex(this.search())) {
            void TelemetryService.send("Search", typeName + "AdvRun");
        } else {
            void TelemetryService.send("Search", typeName + "SelectRun");
            if (typeId === Item.Ids.Task && def.prefixedQuery && !def.prefixedQuery.startsWith("&")) {
                void TelemetryService.send("Search", "Task" + def.prefixedQuery);
            }
        }

        AriaLive.announce(`Searching ${typeName.toLowerCase()}s...`);
        this.setSearchDefinition({ search: def, detached: false });
        this.manuallyRefreshTabComponent(this.store.tab(), typeId);
        this.updateUrlIfNeeded();
    }

    private processInitSearch(initSearch?: string) {
        const typeId = untracked(this.store.typeId);
        // TODO: call blanket `this.onReset({skipUrlChange: !initSearch})` and remove
        //  `fromSavedSearch: undefined` state updates
        if (!initSearch) {
            return untracked(() => this.onReset());
        } else if (initSearch?.startsWith("advanced::")) {
            // FYI, advanced query strings are currently disabled in `this.computeQueryString`
            const [advanced] = S25QLTokenizer.validateQL(decodeURIComponent(initSearch.substring(8)), typeId);
            return untracked(() => {
                if (
                    advanced &&
                    this.store.updateItemState({
                        search: {
                            advanced: true,
                            model: <AdvancedModel>advanced.model,
                            searches: advanced.searches,
                        },
                        // clear any previously selected search
                        fromSavedSearch: undefined,
                        pristine: true,
                    })
                ) {
                    this.validate() && this.executeSearch();
                }
            });
        } else if (initSearch?.startsWith("ql::")) {
            // FYI, QL query strings are currently disabled in `this.computeQueryString`
            return untracked(() => {
                if (this.store.setTextInput(decodeURIComponent(initSearch.substring(2)), { pristine: true })) {
                    // clear any previously selected search
                    this.store.updateItemState({
                        fromSavedSearch: undefined,
                    });
                    this.validate() && this.executeSearch();
                }
            });
        }
        const predefinedSearch = Search.predefined[typeId].includes(initSearch);
        if (!predefinedSearch) {
            // Parse URI-encoded keyword searches
            const keywordRegex = "&(events|spaces|organizations|resources)?_?name=(?<keyword>[^/&]+)";
            const keyword = initSearch?.match(keywordRegex)?.groups?.keyword;
            const criteria = initSearch ? SimpleSearch.Criteria.deserialize(initSearch, typeId) : undefined;
            if (keyword || criteria) {
                return untracked(() => {
                    this.store.setCriteria(criteria);
                    this.store.setTextInput(keyword ? decodeURIComponent(keyword) : undefined);
                    // clear any previously selected search
                    this.store.updateItemState({
                        fromSavedSearch: undefined,
                    });
                    this.validate() && this.executeSearch();
                });
            }
        }
        // fall back to looking for a saved search
        let mappedValue: string = initSearch;
        if (initSearch === "&favorite=T") {
            // Favorite searches need to be prefixed by type
            mappedValue = Search.favorite[typeId] || initSearch;
        } else if (initSearch === "&direct=T&min_ols=R&can_assign=T") {
            mappedValue = "&spaces_direct=T&spaces_min_ols=R&spaces_can_assign=T";
        }
        return untracked(() => this.chooseSavedSearch(mappedValue));
    }

    @Bind
    setSavedSearchesLoaded() {
        this.setSearchDefinition({ savedSearchesLoaded: true });
    }

    private async chooseSavedSearch(value: unknown) {
        let selectedSearch;
        this.setSearchDefinition({ savedSearchesLoaded: false });
        if (this.searchDropdown().itemTypeId !== this.store.typeId()) {
            // :facepalm: because the component needs its input to have finished updating
            // in a sane world, we would set an input instead of directly invoking the component
            await S25Util.delay(0);
        }
        if (this.savedSearchesLoaded()) {
            selectedSearch = await this.searchDropdown().selectSearch(
                this.searchDropdownParams({ andSelectValue: value }),
            );
        } else {
            selectedSearch = await this.searchDropdown().reloadSearches(
                this.searchDropdownParams({ andSelectValue: value }),
            );
        }
        if (selectedSearch) {
            void this.store.chooseSavedSearch(selectedSearch);
        } else {
            console.error("Could not select search. Permissions issue?");
        }
        this.setSearchDefinition({ savedSearchesLoaded: true });
    }

    savedSearchChosen(search: Nullable<DropDownItem<number>>) {
        const chosenPromise = this.store.chooseSavedSearch(search);
        this.validate() && this.executeSearch();
        return chosenPromise;
    }

    private setSearchDefinition({
        search,
        typeId = untracked(this.store.typeId),
        tab,
        savedSearchesLoaded,
        detached,
    }: {
        search?: SearchDefinition;
        typeId?: Search.TypeId;
        tab?: Search.Tab;
        savedSearchesLoaded?: boolean;
        detached?: boolean;
    }) {
        if (search && !tab) {
            tab = untracked(this.store.tab);
        }
        this.searchByType.update((byType) => ({
            ...byType,
            [typeId]: {
                ...(search ?? byType[typeId]),
                detached: detached ?? byType[typeId].detached,
                rendered: tab ? S25Util.array.addUnique(byType[typeId].rendered, tab) : byType[typeId].rendered,
                savedSearchesLoaded: savedSearchesLoaded ?? byType[typeId].savedSearchesLoaded,
            } satisfies SearchConfig,
        }));
    }

    private manuallyRefreshTabComponent(tab: string, typeId: Ids) {
        let components: readonly (Refreshable & HasItemType)[] = [];
        if (tab === "list") {
            components = untracked(this.listComponents);
        } else if (tab === "calendar") {
            components = untracked(this.calendarComponents);
        } else if (tab === "availability") {
            components = untracked(this.availGridComponents);
        } else if (tab === "availabilityWeekly") {
            components = untracked(this.availWeeklyComponents);
        }
        setTimeout(() => {
            components
                .filter((comp) => comp.itemTypeId === typeId)
                .forEach((comp) => {
                    void comp.refresh();
                });
        });
    }

    private computeRenderedVizTypes() {
        return Object.entries(this.searchByType()).reduce(
            (acc: { [tab in Search.Tab]: Search.TypeId[] }, [typeId, config]) => {
                config.rendered.forEach((viz) => {
                    acc[viz] = [...(acc[viz] ?? []), Number(typeId) as Search.TypeId];
                });
                return acc;
            },
            {
                list: [],
                calendar: [],
                availability: [],
                availabilityWeekly: [],
            },
        );
    }

    async itemTypeChosen(dropdownItem: Nullable<DropDownItem<Search.TypeId>>) {
        const itemTypeId = S25Assert.definedValue(dropdownItem?.itemId);
        this.store.setItemType(itemTypeId);
        this.sidebarStore.close();
        this.updateUrlIfNeeded();
    }

    async onTabChange(tab?: Search.Tab) {
        const nextTab = tab || "list";
        if (nextTab === this.store.tab()) return;
        this.store.updateItemState({ tab: nextTab });
        this.setSearchDefinition({ tab: nextTab });
        this.updateUrlIfNeeded();
        const tabLabels: Record<Search.Tab, string> = {
            list: "List",
            calendar: "Calendar",
            availability: "Availability",
            availabilityWeekly: "Availability Weekly",
        };
        await this.focusActiveViewToggle(nextTab);
        AriaLive.announce(`Showing ${tabLabels[nextTab]} view`);
    }

    private async focusActiveViewToggle(tab: Search.Tab) {
        let attempt = 0;
        while (!this.tryFocusActiveViewToggle(tab) && attempt++ < 200) {
            await S25Util.delay(50);
        }
    }

    private tryFocusActiveViewToggle(tab: Search.Tab): boolean {
        const typeId = this.store.typeId();
        let components: readonly {
            itemTypeId: Item.Id;
            focusViewButton?: (viewTab: Search.Tab) => boolean;
        }[] = [];
        if (tab === "list") {
            components = untracked(this.listComponents);
        } else if (tab === "calendar") {
            components = untracked(this.calendarComponents);
        } else if (tab === "availability") {
            components = untracked(this.availGridComponents);
        } else if (tab === "availabilityWeekly") {
            components = untracked(this.availWeeklyComponents);
        }
        const activeComponent = components.find((component) => component.itemTypeId === typeId);
        return activeComponent?.focusViewButton?.(tab) ?? false;
    }

    async onSearch() {
        if (await this.navigateToEventIfSearchIsLocator()) {
            // If we navigated to an event, do nothing further in the search
            return;
        }
        this.validate() && this.executeSearch();
    }

    private async navigateToEventIfSearchIsLocator() {
        const input = this.store.text();
        if (input && this.store.typeId() === Item.Ids.Event) {
            const eventLocator = S25Util.completeEventLocator(input);
            if (eventLocator) {
                const [eventId, error] = await S25Util.Maybe(EventService.getEventIdByLocator(eventLocator));
                if (!(error || !eventId)) {
                    this.store.setTextInput(undefined);
                    void StateService.gotoItem({ itemTypeId: Item.Ids.Event, itemId: eventId });
                    return true;
                }
            }
        }
        return false;
    }

    // TODO: move the advanced criteria into store, then move this validate function there?
    validateAdvancedSearch(): boolean {
        const valid = this.advancedInputComponent()?.validate() ?? true;
        if (!valid) {
            this.store.setError("Invalid Search");
        }
        return valid;
    }

    @Bind
    onReset() {
        const typeId = this.store.typeId();
        this.searchByType.update((byType) => ({
            ...byType,
            [typeId]: {
                ...byType[typeId],
                detached: true,
            } satisfies SearchConfig,
        }));
        this.store.reset();
        this.updateUrlIfNeeded();
    }

    async onAdvancedToggleChange(isAdvanced: Nullable<boolean>) {
        try {
            this.togglingAdvanced.set(true);
            if (isAdvanced) {
                await this.store.convertToAdvanced();
            } else {
                this.store.convertToQL();
            }
        } finally {
            this.togglingAdvanced.set(false);
        }
    }

    save(asNew: boolean = false) {
        if (!this.validate()) return;
        // TODO: lazy load when it's possible :(
        // return import("./save.search.modal.component").then(({SaveSearchModalComponent}) => {
        // TODO: use a return value instead of passing a callback
        void ModalService.byClass(SaveSearchModalComponent, {
            onSaved: (queryId) => {
                this.searchDropdown().reloadSearches(this.searchDropdownParams({ andSelectValue: queryId }));
            },
            initialName: this.store.fromSavedSearch()?.itemName || "",
            asNew,
            initialFav: asNew ? true : this.store.fromSavedSearch()?.favorite === "T",
        });
        // });
    }

    private searchDropdownParams(params: { andSelectValue: unknown; typeId?: Search.TypeId }): ReloadAndSelectParams;
    private searchDropdownParams(params?: { andSelectValue?: unknown; typeId?: Search.TypeId }): ReloadParams;
    private searchDropdownParams(params?: {
        andSelectValue?: unknown;
        typeId?: Search.TypeId;
    }): ReloadAndSelectParams | ReloadParams {
        if (params?.andSelectValue) {
            return {
                itemTypeId: params.typeId ?? untracked(this.store.typeId),
                property: S25Util.isNumeric(params.andSelectValue) ? "itemId" : "val",
                value: params.andSelectValue,
            };
        } else {
            return {
                itemTypeId: params?.typeId ?? untracked(this.store.typeId),
            };
        }
    }

    async onExport() {
        if (this.store.tab() !== "list") {
            alert(
                "Exporting is only available from the list view. Please switch to the list view to export your results.",
            );
            return;
        }
        this.exporting.set(true);
        const report = this.store.report();
        let reportId: number = report.rpt_id;
        const isPredefined = this.store.isPredefinedSearch();
        const isTemp = !isPredefined && (!this.store.fromSavedSearch()?.itemId || !this.store.savedSearch());
        let queryId: number | undefined;
        let query: string = "";

        // TODO: make this if/else statement make more sense
        if (this.store.typeName() === "task" && isPredefined) {
            const type = String(this.store.fromSavedSearch()!.val).toLowerCase() as keyof typeof Report.taskQueryMap;
            query = `&search=${Report.taskQueryMap[type]}`;
        } else if (isTemp) {
            const complexSearch = this.searchConfig().complex;
            if (complexSearch) {
                const newSearch = await SearchService.createSearch({
                    model: S25Util.deepCopy(complexSearch.model),
                    searches: S25Util.deepCopy(complexSearch.searches || {}),
                    typeId: this.store.typeId(),
                });
                queryId = newSearch.queryId;
            }
        } else {
            queryId = Number(this.store.fromSavedSearch()?.itemId);
        }

        if (this.store.typeName() === "task") {
            const userId = await ContactService.getCurrentId();
            if (queryId) {
                query = `&user_id=${userId}&query_id=${queryId}`;
            } else {
                query += `&user_id=${userId}`;
            }
        }

        if ([Ids.Event, Ids.Location, Ids.Resource, Ids.Organization, Ids.Task].includes(this.store.typeId())) {
            this.reportOptions.set(await ReportService.getQueryReports(this.store.typeId() as number as ReportType));
            const reportCount = this.reportOptions()?.length ?? 0;
            // Only show selection if there's more than one option
            if (reportCount > 1) {
                this.exporting.set(false);
                const chosenReport = await this.exportResultsModal().open();
                if (!chosenReport) return;
                reportId = chosenReport;
            } // TODO?: else { reportId = this.reportOptions()[0] }
        }
        this.exporting.set(true);
        await this.runReport("paramName" in report ? { [report.paramName]: queryId } : {}, reportId, query);
        if (isTemp) {
            void SearchService.deleteSearch(queryId!, this.store.typeId());
        }
        this.exporting.set(false);
    }

    private async runReport(params: Record<string, unknown>, reportId: number, query: string) {
        const listComponent = this.listComponents().find((c) => c.itemTypeId === this.store.typeId());
        S25Assert.isDefined(listComponent);
        // skip the network call by pulling directly from list component
        // TODO: expose the count from other components (or make the data call from this component?)
        const count = listComponent.getResultCount();
        if (!count) {
            alert(`There is no data to process yet. If a search is running, please wait for it to complete.`);
        } else if (count > REPORT_LIMIT) {
            alert(
                `This search contains too many items. Please restrict your search to ${REPORT_LIMIT} or fewer items${
                    this.store.typeId() !== Item.Ids.Task
                        ? ", or save the search and run the report from the reports page"
                        : ""
                }.`,
            );
        } else {
            const report: Report.SimpleObject = { rpt_id: reportId, rpt_engine: "WS", isAsync: false };
            const dateView = listComponent.dateView;
            if (dateView === "futureOnly") {
                params.date_parm1 = S25Util.date.toS25ISODateStr(S25Util.date.currentDate());
            } else if (dateView === "recentHistory") {
                params.date_parm1 = S25Util.date.toS25ISODateStr(
                    S25Util.date.addMonths(S25Util.date.currentDate(), -18),
                );
            }
            NotificationService.post("Report will be downloaded upon completion");
            await ReportService.runReportRequest({
                report: report,
                context: params,
                format: null,
                delivery: null,
                otherRecipients: null,
                otherMessage: null,
                query: query,
            });
        }
    }

    computeSearchActions(): DropDownItem<Search.Action>[] {
        const actions: DropDownItem<Search.Action>[] = [];
        if (this.store.buttonPerms().share) {
            actions.push({ itemName: "Share", itemId: "share", action: () => this.shareSearch() });
        }
        if (this.store.buttonPerms().publish) {
            actions.push({ itemName: "Publish", itemId: "publish", action: () => this.publishSearch() });
        }
        actions.push({
            itemName: "Refresh Search Criteria",
            itemId: "refresh",
            action: () => this.refreshSearch(),
        });
        if (this.store.buttonPerms().delete) {
            actions.push({ itemName: "Delete", itemId: "delete", action: () => this.deleteSearch() });
        }
        return actions;
    }

    // TODO: make a dropdown button component
    handleSearchAction(dropdownItem: Nullable<DropDownItem<Search.Action>>) {
        dropdownItem?.action();
        setTimeout(() => {
            this.searchAction.set(undefined);
        });
    }

    shareSearch() {
        try {
            this.assertHasValidSavedSearch({ verb: "share", verbing: "sharing" });
            return this.shareSearchModal().open();
        } catch (e) {
            S25Util.showError(e);
        }
    }

    async publishSearch() {
        try {
            this.assertHasValidSavedSearch({ verb: "publish" });
            return this.publishSearchModal()?.open();
        } catch (e) {
            S25Util.showError(e);
        }
    }

    refreshSearch() {
        if (this.store.advancedSearch()) {
            return this.store.convertToAdvanced();
        } else if (this.store.qlSearch()) {
            return this.store.convertToQL();
        }
    }

    private assertHasValidSavedSearch(
        params: HasValidSavedSearch & { requireName: true },
    ): DropDownItem<number> & { itemName: string };
    private assertHasValidSavedSearch(params: HasValidSavedSearch): DropDownItem<number>;
    private assertHasValidSavedSearch(params: HasValidSavedSearch): DropDownItem<number> {
        const from = this.store.fromSavedSearch();
        if (from?.itemId && (!params.requireOwner || from.isOwner) && (!params.requireName || from.itemName)) {
            return from;
        }
        let errorMessage = `Please select a valid search ${params.requireOwner ? "that you own " : ""}to ${params.verb}`;
        if (from) {
            errorMessage += ` (Pre-Defined searches are not valid for ${params.verbing ?? params.verb + "ing"})`;
        }
        throw new Error(errorMessage);
    }

    async deleteSearch() {
        try {
            const { itemId, itemName } = this.assertHasValidSavedSearch({
                verb: "delete",
                verbing: "deleting",
                requireOwner: true,
                requireName: true,
            });
            await SearchService.deleteSearchWithConf(Number(itemId), itemName, this.store.typeId(), () =>
                this.store.reset(),
            );
            await this.searchDropdown().reloadSearches(this.searchDropdownParams());
        } catch (error) {
            S25Util.showError(error);
        }
    }

    onAiSearchQuery(query: string) {
        this.store.setTextInput(query);
        this.validate() && this.executeSearch();
    }

    refreshAdvancedSearch() {
        // TODO: move advanced search model into the store instead of just
        //  doing a simple spread to trigger memoization
        const advancedSearch = this.store.advancedSearch();
        if (advancedSearch) {
            this.store.updateItemState({
                search: {
                    ...advancedSearch,
                },
            });
        }
    }
}
